import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import Button from './Button';

interface PropertyType {
  categories: string[];
  subtypes: string[];
}

interface SubscriptionFormData {
  fullName: string;
  email: string;
  companyName: string;
  phoneNumber: string;
  zipcodes: string;
  propertyType: PropertyType;
  numberOfUnits: number;
  comments: string;
}

interface SubscriptionFormProps {
  onClose: () => void;
  planTitle: string;
}

const SubscriptionForm: React.FC<SubscriptionFormProps> = ({ onClose, planTitle }) => {
  const [formData, setFormData] = useState<SubscriptionFormData>({
    fullName: '',
    email: '',
    companyName: '',
    phoneNumber: '',
    zipcodes: '',
    propertyType: { categories: ['residential'], subtypes: ['house'] },
    numberOfUnits: 1,
    comments: '',
  });

  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showSubtypeDropdown, setShowSubtypeDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allCategories = [
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'land', label: 'Land' },
    { value: 'other', label: 'Other' }
  ];

  const subtypesByCategory = {
    residential: [
      { value: 'multifamily-2-4', label: 'Multifamily (2-4 units)' },
      { value: 'apartment', label: 'Apartment' },
      { value: 'condo', label: 'Condo' },
      { value: 'house', label: 'House' }
    ],
    commercial: [
      { value: 'multifamily-5plus', label: 'Multifamily (5+ units)' },
      { value: 'office', label: 'Office' },
      { value: 'retail', label: 'Retail' },
      { value: 'healthcare', label: 'Health Care' },
      { value: 'industrial', label: 'Industrial' }
    ],
    land: [
      { value: 'vacant-lot', label: 'Vacant Lot' },
      { value: 'agricultural', label: 'Agricultural' },
      { value: 'development', label: 'Development Land' }
    ],
    other: [
      { value: 'mixed-use', label: 'Mixed Use' },
      { value: 'hospitality', label: 'Hospitality' },
      { value: 'storage', label: 'Storage/Warehouse' },
      { value: 'special-purpose', label: 'Special Purpose' }
    ]
  };

  const getAvailableSubtypes = () => {
    const availableSubtypes: { value: string; label: string; category: string }[] = [];
    formData.propertyType.categories.forEach(category => {
      const categorySubtypes = subtypesByCategory[category as keyof typeof subtypesByCategory] || [];
      categorySubtypes.forEach(subtype => {
        availableSubtypes.push({ ...subtype, category });
      });
    });
    return availableSubtypes;
  };

  const handleCategoryChange = (categoryValue: string) => {
    const currentCategories = formData.propertyType.categories;
    let newCategories;

    if (currentCategories.includes(categoryValue)) {
      newCategories = currentCategories.filter(cat => cat !== categoryValue);
    } else {
      newCategories = [...currentCategories, categoryValue];
    }

    const availableSubtypes = getAvailableSubtypes();
    const validSubtypes = formData.propertyType.subtypes.filter(subtype =>
      availableSubtypes.some(available => available.value === subtype)
    );

    setFormData(prev => ({
      ...prev,
      propertyType: {
        categories: newCategories,
        subtypes: validSubtypes.length > 0 ? validSubtypes : ['house']
      }
    }));
  };

  const handleSubtypeChange = (subtypeValue: string) => {
    const currentSubtypes = formData.propertyType.subtypes;
    let newSubtypes;

    if (currentSubtypes.includes(subtypeValue)) {
      newSubtypes = currentSubtypes.filter(sub => sub !== subtypeValue);
    } else {
      newSubtypes = [...currentSubtypes, subtypeValue];
    }

    setFormData(prev => ({
      ...prev,
      propertyType: {
        ...prev.propertyType,
        subtypes: newSubtypes.length > 0 ? newSubtypes : ['house']
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append('form-name', 'subscription');
      formDataObj.append('plan_title', planTitle);
      formDataObj.append('full_name', formData.fullName);
      formDataObj.append('email', formData.email);
      formDataObj.append('company_name', formData.companyName);
      formDataObj.append('phone_number', formData.phoneNumber);
      formDataObj.append('zipcodes', formData.zipcodes);
      formDataObj.append('property_categories', formData.propertyType.categories.join(', '));
      formDataObj.append('property_types', formData.propertyType.subtypes.join(', '));
      formDataObj.append('number_of_units', formData.numberOfUnits.toString());
      formDataObj.append('comments', formData.comments);

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataObj as any).toString(),
      });

      if (response.ok) {
        toast.success('Inquiry submitted successfully!');
        onClose();
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-abadi text-2xl font-medium">Subscribe to {planTitle}</h3>
          <button
            onClick={onClose}
            className="text-dark-gray hover:text-dark"
          >
            ✕
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          name="subscription"
          method="POST"
          data-netlify="true"
          netlify-honeypot="bot-field"
          className="space-y-4"
        >
          <input type="hidden" name="form-name" value="subscription" />
          <p className="hidden">
            <label>Don't fill this out: <input name="bot-field" /></label>
          </p>
          <input type="hidden" name="plan_title" value={planTitle} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-dark-gray mb-1">
                Full Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                name="full_name"
                required
                value={formData.fullName}
                onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                className="w-full px-4 py-2 rounded-md border border-medium-gray focus:outline-none focus:border-secondary"
              />
            </div>

            <div>
              <label className="block text-dark-gray mb-1">
                Email <span className="text-error">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-2 rounded-md border border-medium-gray focus:outline-none focus:border-secondary"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-dark-gray mb-1">
                Company Name
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.companyName}
                onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                className="w-full px-4 py-2 rounded-md border border-medium-gray focus:outline-none focus:border-secondary"
              />
            </div>

            <div>
              <label className="block text-dark-gray mb-1">
                Phone Number <span className="text-error">*</span>
              </label>
              <input
                type="tel"
                name="phone_number"
                required
                value={formData.phoneNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                className="w-full px-4 py-2 rounded-md border border-medium-gray focus:outline-none focus:border-secondary"
              />
            </div>
          </div>

          <div>
            <label className="block text-dark-gray mb-1">
              Zipcode(s) of Rental(s) <span className="text-error">*</span>
            </label>
            <input
              type="text"
              name="zipcodes"
              required
              placeholder="Enter multiple zipcodes separated by commas"
              value={formData.zipcodes}
              onChange={(e) => setFormData(prev => ({ ...prev, zipcodes: e.target.value }))}
              className="w-full px-4 py-2 rounded-md border border-medium-gray focus:outline-none focus:border-secondary"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-dark-gray mb-1">
                Property Categories <span className="text-error">*</span>
              </label>
              <input
                type="hidden"
                name="property_categories"
                value={formData.propertyType.categories.join(', ')}
              />
              <div
                className="w-full px-4 py-2 rounded-md border border-medium-gray focus:outline-none focus:border-secondary cursor-pointer bg-white"
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
              >
                <div className="flex justify-between items-center">
                  <span className="text-sm">
                    {formData.propertyType.categories.length > 0
                      ? formData.propertyType.categories.map(cat =>
                          allCategories.find(c => c.value === cat)?.label
                        ).join(', ')
                      : 'Select categories...'}
                  </span>
                  <span className="text-gray-400">▼</span>
                </div>
              </div>

              {showCategoryDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-medium-gray rounded-md shadow-lg">
                  {allCategories.map(category => (
                    <label
                      key={category.value}
                      className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.propertyType.categories.includes(category.value)}
                        onChange={() => handleCategoryChange(category.value)}
                        className="mr-3"
                      />
                      {category.label}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <label className="block text-dark-gray mb-1">
                Property Types <span className="text-error">*</span>
              </label>
              <input
                type="hidden"
                name="property_types"
                value={formData.propertyType.subtypes.join(', ')}
              />
              <div
                className="w-full px-4 py-2 rounded-md border border-medium-gray focus:outline-none focus:border-secondary cursor-pointer bg-white"
                onClick={() => setShowSubtypeDropdown(!showSubtypeDropdown)}
              >
                <div className="flex justify-between items-center">
                  <span className="text-sm">
                    {formData.propertyType.subtypes.length > 0
                      ? formData.propertyType.subtypes.map(subtype => {
                          const availableSubtypes = getAvailableSubtypes();
                          return availableSubtypes.find(s => s.value === subtype)?.label;
                        }).filter(Boolean).join(', ')
                      : 'Select types...'}
                  </span>
                  <span className="text-gray-400">▼</span>
                </div>
              </div>

              {showSubtypeDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-medium-gray rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {getAvailableSubtypes().map(subtype => (
                    <label
                      key={subtype.value}
                      className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.propertyType.subtypes.includes(subtype.value)}
                        onChange={() => handleSubtypeChange(subtype.value)}
                        className="mr-3"
                      />
                      <div>
                        <div>{subtype.label}</div>
                        <div className="text-xs text-gray-500">
                          {allCategories.find(c => c.value === subtype.category)?.label}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-dark-gray mb-1">
              Number of Units <span className="text-error">*</span>
            </label>
            <input
              type="number"
              name="number_of_units"
              required
              min="1"
              value={formData.numberOfUnits}
              onChange={(e) => setFormData(prev => ({ ...prev, numberOfUnits: parseInt(e.target.value) }))}
              className="w-full px-4 py-2 rounded-md border border-medium-gray focus:outline-none focus:border-secondary"
            />
          </div>

          <div>
            <label className="block text-dark-gray mb-1">
              Additional Comments
            </label>
            <textarea
              name="comments"
              rows={4}
              value={formData.comments}
              onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
              className="w-full px-4 py-2 rounded-md border border-medium-gray focus:outline-none focus:border-secondary"
              placeholder="Any specific requirements or questions?"
            ></textarea>
          </div>

          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
            </Button>
          </div>
        </form>
      </div>

      {(showCategoryDropdown || showSubtypeDropdown) && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => {
            setShowCategoryDropdown(false);
            setShowSubtypeDropdown(false);
          }}
        />
      )}
    </div>
  );
};

export default SubscriptionForm;
